/**
 * A page shows at most this many blocks (paragraphs, lists, glyphs, controls…), even when more
 * would fit: short pages keep a learner's attention on one idea at a time. Headings are free.
 * An authored section (between `---` breaks) is never split to honor it.
 */
export const MAX_SLIDE_PAGE_BLOCKS = 2;

/** Blocks that introduce what follows them: they never end a page, and are free against the limit. */
const HEADING_BLOCK_KINDS = new Set(["h1", "h2", "h3", "h4", "h5", "h6", "SlideTitle"]);

/**
 * Blocks that only configure the slide (its footer, its button label) and render nothing. They
 * take no room on any page; the slide renders them once, with its first page.
 */
export const SLIDE_SETTING_BLOCK_KINDS: ReadonlySet<string> = new Set([
  "SlideFooter",
  "SlideOptions",
]);

/** One top-level block of a slide, as compiled (`block`, `section`, `kind`) and measured. */
export type SlidePageBlockLayout = {
  block: number;
  /** Measured height at the page's width and the learner's text size. */
  height: number;
  /**
   * The block introduces the next one (its text ends in a colon), so a page never ends on it.
   * Unlike a heading, it still counts against the block limit.
   */
  keepWithNext?: boolean;
  /** Tag or component name: `p`, `h2`, `ul`, `SvgGlyph`, … */
  kind: string;
  /** Sections are separated by authored `---` breaks; a page never splits one that fits it. */
  section: number;
};

function isHeading(block: SlidePageBlockLayout | undefined): boolean {
  return block !== undefined && HEADING_BLOCK_KINDS.has(block.kind);
}

/** Headings and lead-ins (`keepWithNext`) never end a page while a block follows them. */
function staysWithNext(block: SlidePageBlockLayout | undefined): boolean {
  return block !== undefined && (isHeading(block) || block.keepWithNext === true);
}

function stackHeight(blocks: readonly SlidePageBlockLayout[], gap: number): number {
  return blocks.reduce((total, block, index) => total + block.height + (index === 0 ? 0 : gap), 0);
}

function blockCount(blocks: readonly SlidePageBlockLayout[]): number {
  return blocks.filter((block) => !isHeading(block)).length;
}

/** A heading or lead-in ending a group joins the group after it, so it never ends a page. */
function attachTrailingLeadIns(
  groups: readonly SlidePageBlockLayout[][],
): SlidePageBlockLayout[][] {
  const attached: SlidePageBlockLayout[][] = [];
  let carried: SlidePageBlockLayout[] = [];
  for (const [index, group] of groups.entries()) {
    const merged = [...carried, ...group];
    if (index < groups.length - 1 && staysWithNext(merged.at(-1))) {
      carried = merged;
      continue;
    }
    carried = [];
    attached.push(merged);
  }
  return attached;
}

/** Split a group taller than a page between blocks, keeping each lead-in with what follows it. */
function splitByHeight(
  group: readonly SlidePageBlockLayout[],
  { gap, pageHeight }: { gap: number; pageHeight: number },
): SlidePageBlockLayout[][] {
  const chunks: SlidePageBlockLayout[][] = [];
  let chunk: SlidePageBlockLayout[] = [];
  for (const block of group) {
    if (chunk.length > 0 && stackHeight([...chunk, block], gap) > pageHeight) {
      // Trailing lead-ins carry over with the block that would have followed them.
      let end = chunk.length;
      while (end > 1 && staysWithNext(chunk[end - 1])) {
        end -= 1;
      }
      chunks.push(chunk.slice(0, end));
      chunk = chunk.slice(end);
    }
    chunk.push(block);
  }
  if (chunk.length > 0) {
    chunks.push(chunk);
  }
  return chunks;
}

/**
 * Split a slide's measured blocks into pages, returning each page's block numbers in order.
 *
 * Without `---` breaks, any two blocks may land on different pages. Once a slide is divided into
 * sections, pages break only between sections, and adjacent sections share a page when together
 * they stay within the block limit and the page height. A group taller than a page is the one
 * exception: it is split between blocks, so nothing is cut off. A single block taller than a page
 * gets a page of its own, which scrolls.
 *
 * Setting blocks ({@link SLIDE_SETTING_BLOCK_KINDS}) are left out of every page.
 */
export function paginateSlideBlocks({
  blocks,
  gap,
  maxBlocksPerPage = MAX_SLIDE_PAGE_BLOCKS,
  pageHeight,
}: {
  blocks: readonly SlidePageBlockLayout[];
  /** Vertical space between two blocks on a page. */
  gap: number;
  maxBlocksPerPage?: number;
  /** Height available to a page's blocks. */
  pageHeight: number;
}): number[][] {
  const content = blocks
    .filter((block) => !SLIDE_SETTING_BLOCK_KINDS.has(block.kind))
    .sort((a, b) => a.block - b.block);
  if (content.length === 0) {
    return [[]];
  }

  const isSectioned = new Set(content.map((block) => block.section)).size > 1;
  const groups: SlidePageBlockLayout[][] = [];
  for (const block of content) {
    const last = groups.at(-1);
    if (isSectioned && last !== undefined && last[0]?.section === block.section) {
      last.push(block);
    } else {
      groups.push([block]);
    }
  }

  const pages: SlidePageBlockLayout[][] = [];
  let page: SlidePageBlockLayout[] = [];
  for (const group of attachTrailingLeadIns(groups)) {
    const combined = [...page, ...group];
    if (
      page.length > 0 &&
      blockCount(combined) <= maxBlocksPerPage &&
      stackHeight(combined, gap) <= pageHeight
    ) {
      page = combined;
      continue;
    }
    if (page.length > 0) {
      pages.push(page);
    }
    const chunks = splitByHeight(group, { gap, pageHeight });
    pages.push(...chunks.slice(0, -1));
    page = chunks.at(-1) ?? [];
  }
  if (page.length > 0) {
    pages.push(page);
  }
  return pages.map((blocksOnPage) => blocksOnPage.map((block) => block.block));
}
